import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Clients-figma.css';
import { FaEye, FaEdit, FaTrash, FaSort, FaSearch, FaUserCircle, FaRegCalendarAlt, FaEnvelope, FaSortUp, FaSortDown, FaComment, FaEllipsisH, FaTrashAlt, FaChevronLeft, FaChevronRight, FaBan, FaPlay } from 'react-icons/fa';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';
import { useClients } from '../hooks/useClients';
import { useToast } from '../components/ToastContainer';
import { useSearch } from '../contexts/SearchContext';
import { API_BASE_URL, getTenantId } from '../config/api';
import ViewToggle from '../components/ViewToggle';


// Utilitaire pour générer les avatars
const generateAvatar = (name) => {
  const words = name.split(' ');
  const initials = words.length > 1 
    ? words[0][0] + words[1][0] 
    : words[0][0] + (words[0][1] || '');
  
  const colors = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#F97316'];
  const colorIndex = name.length % colors.length;
  
  return {
    avatar: initials.toUpperCase(),
    avatarColor: colors[colorIndex]
  };
};

const Clients = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { globalSearch } = useSearch();
  
  // États locaux pour les filtres et UI
  const [selectedStatus, setSelectedStatus] = useState('Tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedType, setSelectedType] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedClients, setSelectedClients] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', clientId: null, clientName: '' });
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Hook pour gérer les clients avec API
  const { 
    clients: apiClients, 
    loading: clientsLoading, 
    error: clientsError,
    pagination,
    deleteClient,
    toggleClientStatus,
    updateFilters,
    refetch
  } = useClients({
    status: selectedStatus !== 'Tous' ? selectedStatus : undefined,
    type: selectedType || undefined,
    search: searchTerm || undefined,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction
  });

  // Utiliser les données API avec traitement pour l'affichage
  const processClientsData = (clients) => {
    return clients.map((client, index) => ({
      ...client,
      // Générer un ID numérique pour l'affichage
      displayId: index + 1,
      // L'API retourne déjà le champ 'name' avec le nom complet, on le garde tel quel
      name: client.name || `Client ${index + 1}`,
      // Le statut et autres champs sont déjà au bon format dans l'API
      // Mapper la date si elle vient dans un format différent
      date: client.date ? new Date(client.date).toLocaleDateString('fr-FR') : '',
      // Les services et reports sont déjà fournis par l'API
      services: client.services || 0,
      reports: client.reports || 0
    }));
  };

  const clientsData = apiClients && apiClients.length > 0 
    ? processClientsData(apiClients) 
    : [];
  
  

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    // Mettre à jour les filtres API
    updateFilters({
      sortBy: key,
      sortOrder: direction
    });
  };

  // Fonction de filtrage et tri
  const getFilteredAndSortedClients = () => {
    let filtered = clientsData.filter(client => {
      const matchesStatus = selectedStatus === 'Tous' || client.status === selectedStatus;
      const matchesType = !selectedType || client.type === selectedType;
      const matchesLocalSearch = !searchTerm || 
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phone.includes(searchTerm);
      const matchesGlobalSearch = !globalSearch || 
        client.name.toLowerCase().includes(globalSearch.toLowerCase()) ||
        client.email.toLowerCase().includes(globalSearch.toLowerCase()) ||
        client.phone.includes(globalSearch);
      
      return matchesStatus && matchesType && matchesLocalSearch && matchesGlobalSearch;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  };

  // Fonction de sélection
  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Sélection multiple
  const handleSelectAll = () => {
    const filteredClients = getFilteredAndSortedClients();
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(client => client.id));
    }
  };

  // Gestion des actions
  const handleDeleteClient = (clientId, clientName) => {
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      clientId,
      clientName,
      title: 'Supprimer le client',
      message: `Êtes-vous sûr de vouloir supprimer le client "${clientName}" ? Cette action est irréversible.`
    });
  };

  const handleSuspendClient = (clientId, clientName, currentStatus) => {
    const isSuspending = currentStatus !== 'Suspendu';
    setConfirmModal({
      isOpen: true,
      type: isSuspending ? 'suspend' : 'reactivate',
      clientId,
      clientName,
      title: isSuspending ? 'Suspendre le client' : 'Réactiver le client',
      message: isSuspending 
        ? `Êtes-vous sûr de vouloir suspendre le client "${clientName}" ?`
        : `Êtes-vous sûr de vouloir réactiver le client "${clientName}" ?`
    });
  };

  const handleConfirmAction = async () => {
    const { type, clientId, clientName } = confirmModal;
    
    try {
      switch (type) {
        case 'delete':
          const deleteSuccess = await deleteClient(clientId);
          if (deleteSuccess) {
            showSuccess('Client supprimé', `Le client "${clientName}" a été supprimé avec succès.`);
          }
          break;
        case 'suspend':
          const suspendSuccess = await toggleClientStatus(clientId, 'Suspendu');
          if (suspendSuccess) {
            showSuccess('Client suspendu', `Le client "${clientName}" a été suspendu.`);
          }
          break;
        case 'reactivate':
          const reactivateSuccess = await toggleClientStatus(clientId, 'Actif');
          if (reactivateSuccess) {
            showSuccess('Client réactivé', `Le client "${clientName}" a été réactivé.`);
          }
          break;
      }
    } catch (error) {
      showError('Erreur', `Impossible d'effectuer cette action: ${error.message}`);
    }
    
    setConfirmModal({ isOpen: false, type: '', clientId: null, clientName: '' });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, type: '', clientId: null, clientName: '' });
  };

  // Gestion de la messagerie
  const handleSendMessage = (client) => {
    setSelectedClient(client);
    setShowMessageModal(true);
  };

  // Gestion des changements de filtres
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    updateFilters({ 
      status: status !== 'Tous' ? status : undefined 
    });
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    updateFilters({ 
      type: type || undefined 
    });
  };

  // Fonction d'export CSV
  const handleExportCSV = async () => {
    try {
      let allClients = [];
      
      if (clientsData.length > 0) {
        // Utiliser les données actuelles filtrées
        allClients = getFilteredAndSortedClients();
        console.log('Export avec données actuelles:', allClients.length, 'clients');
      } else {
        // Récupérer tous les clients avec les mêmes filtres mais sans pagination
        // Utiliser API_BASE_URL importé depuis config/api
        const params = new URLSearchParams();
        
        if (selectedStatus !== 'Tous') params.append('status', selectedStatus);
        if (selectedType) params.append('type', selectedType);
        if (searchTerm) params.append('search', searchTerm);
        if (sortConfig.key) params.append('sortBy', sortConfig.key);
        if (sortConfig.direction) params.append('sortOrder', sortConfig.direction);
        params.append('page', '1');
        params.append('limit', '1000'); // Grande limite pour récupérer tous les résultats
        
        console.log('URL API export:', `${API_BASE_URL}/clients?${params.toString()}`);
        
        const response = await fetch(`${API_BASE_URL}/clients?${params.toString()}`, {
          headers: {
            'x-tenant-id': 'bfc2a969-f920-4a51-b8b9-76fe5be6da1b' // Utiliser le tenant-id approprié
          }
        });
        
        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Réponse API export:', data);
        allClients = processClientsData(data.clients || data.data || []);
        console.log('Clients traités pour export:', allClients.length);
      }

      // Vérifier qu'on a des données
      if (!allClients || allClients.length === 0) {
        showError('Aucune donnée', 'Aucun client à exporter avec les filtres actuels.');
        return;
      }

      console.log('Préparation CSV pour', allClients.length, 'clients');

      // Créer le contenu CSV
      const csvHeaders = [
        'ID',
        'Nom et prénom',
        'Email',
        'Téléphone',
        'Type',
        'Date d\'inscription',
        'Services demandés',
        'Signalements',
        'Statut'
      ];

      const csvRows = allClients.map(client => [
        client.displayId || client.id,
        `"${client.name || ''}"`,
        `"${client.email || ''}"`,
        `"${client.phone || ''}"`,
        `"${client.type || ''}"`,
        `"${client.date || ''}"`,
        client.services || 0,
        client.reports || 0,
        `"${client.status || ''}"`
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');

      // Créer et télécharger le fichier
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccess('Export réussi', `${allClients.length} clients exportés avec succès.`);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showError('Erreur d\'export', 'Impossible d\'exporter les données. Veuillez réessayer.');
    }
  };


  // Configuration des colonnes pour le tableau responsive
  const columns = [
    {
      header: '',
      key: 'checkbox',
      render: (client) => (
        <input 
          type="checkbox" 
          checked={selectedClients.includes(client.id)}
          onChange={() => handleSelectClient(client.id)}
        />
      )
    },
    {
      header: 'ID',
      key: 'id',
      render: (client) => (
        <span className="client-id">{client.displayId}</span>
      )
    },
    {
      header: 'Nom et prénom',
      key: 'name',
      render: (client) => {
        const avatar = client.avatar && client.avatarColor 
          ? { avatar: client.avatar, avatarColor: client.avatarColor }
          : generateAvatar(client.name);
        
        return (
          <div className="name-avatar">
            <div 
              className="avatar-circle" 
              style={{ backgroundColor: avatar.avatarColor }}
            >
              {avatar.avatar}
            </div>
            {client.name}
          </div>
        );
      }
    },
    { header: 'Email', key: 'email' },
    { header: 'Téléphone', key: 'phone' },
    { header: 'Type', key: 'type' },
    { header: 'Date d\'inscription', key: 'date' },
    { header: 'Services Demandés', key: 'services' },
    { header: 'Signalements', key: 'reports' },
    {
      header: 'Statut',
      key: 'status',
      render: (client) => (
        <span className={`status-badge ${client.status.toLowerCase() === 'suspendu' ? 'status-inactive' : 'status-active'}`}>
          {client.status}
        </span>
      )
    },
    {
      header: 'Actions',
      key: 'actions',
      render: (client) => (
        <div className="actions">
          <a 
            href="#!" 
            className={`status-action ${client.status === 'Suspendu' ? 'reactivate' : 'suspend'}`}
            onClick={(e) => {
              e.preventDefault();
              handleSuspendClient(client.id, client.name, client.status);
            }}
          >
            {client.status === 'Suspendu' ? 'Réactiver' : 'Suspendre'}
          </a>
          <div className="action-icons">
            <FaEye 
              className="icon view-icon" 
              title="Voir" 
              onClick={() => navigate(`/clients/${client.id}`)}
            />
            <FaComment 
              className="icon message-icon" 
              title="Message"
              onClick={() => handleSendMessage(client)}
            />
            <FaTrashAlt 
              className="icon delete-icon" 
              title="Supprimer"
              onClick={() => handleDeleteClient(client.id, client.name)}
            />
          </div>
        </div>
      )
    }
  ];

  // Configuration pour les cartes mobile
  const mobileCardConfig = {
    title: (client) => client.name,
    subtitle: (client) => client.email,
    fields: [
      { label: 'Téléphone', key: 'phone' },
      { label: 'Type', key: 'type' },
      { label: 'Services', key: 'services' },
      { label: 'Signalements', key: 'reports' },
      { 
        label: 'Statut', 
        render: (client) => (
          <span className={`status-badge ${client.status === 'Suspendu' ? 'status-inactive' : 'status-active'}`}>
            {client.status}
          </span>
        )
      }
    ],
    actions: [
      {
        label: 'Voir',
        icon: <FaEye />,
        className: 'btn-primary',
        onClick: (client) => navigate(`/clients/${client.id}`)
      },
      {
        label: 'Éditer',
        icon: <FaEdit />,
        className: 'btn-secondary',
        onClick: (client) => navigate(`/clients/${client.id}`)
      },
      {
        label: 'Message',
        icon: <FaComment />,
        className: 'btn-secondary',
        onClick: (client) => handleSendMessage(client)
      },
      {
        label: 'Suspendre/Réactiver',
        className: 'btn-warning',
        onClick: (client) => handleSuspendClient(client.id, client.name, client.status)
      },
      {
        label: 'Supprimer',
        icon: <FaTrashAlt />,
        className: 'btn-danger',
        onClick: (client) => handleDeleteClient(client.id, client.name)
      }
    ]
  };

  return (
    <div className="clients-container">
      
      <div className="clients-header-line">
        <h2 className="clients-title">Clients</h2>

        <div className="clients-filters">
          <div className="date-inscription-container">
            <label htmlFor="date-inscription">Date d'inscription :</label>
            <div className="date-input-wrapper">
              <FaRegCalendarAlt className="calendar-icon" />
              <input type="date" id="date-inscription" className="date-input" />
            </div>
          </div>

          <select 
            className="client-type-select"
            value={selectedType}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="">Type client</option>
            <option value="Particulier">Particulier</option>
            <option value="Professionnel">Professionnel</option>
          </select>

          <div className="client-status-toggle">
            {['Tous', 'Actif', 'Suspendu'].map((status) => (
              <button
                key={status}
                className={`status-btn ${selectedStatus === status ? 'active' : ''}`}
                onClick={() => handleStatusChange(status)}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="clients-search-section">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input 
            className="search-bar" 
            placeholder="Recherche nom, email, tel ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
        <button className="export-btn" onClick={handleExportCSV}>Export CSV</button>
      </div>

      {viewMode === 'cards' ? (
        <div className="clients-grid">
          {clientsLoading ? (
            <div className="loading">Chargement des clients...</div>
          ) : getFilteredAndSortedClients().length === 0 ? (
            <div className="no-results"><p>Aucun client trouvé</p></div>
          ) : (
            getFilteredAndSortedClients().map((client) => {
              const avatar = client.avatar && client.avatarColor
                ? { avatar: client.avatar, avatarColor: client.avatarColor }
                : generateAvatar(client.name);
              return (
                <div
                  key={client.id}
                  className="card-client"
                  onClick={() => navigate(`/clients/${client.id}`)}
                >
                  <div className="card-client-header">
                    <div className="card-client-left">
                      <div className="avatar-circle" style={{ backgroundColor: avatar.avatarColor }}>
                        {avatar.avatar}
                      </div>
                      <div>
                        <h3 className="card-client-name">
                          {client.name}
                          <span className={`badge-status ${client.status?.toLowerCase() === 'suspendu' ? 'status-inactive' : 'status-active'}`}>
                            {client.status}
                          </span>
                        </h3>
                        <p className="card-client-id">ID : {client.displayId}</p>
                        <span className="card-client-type">{client.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="card-client-contact">
                    <span className="card-client-email">
                      <FaEnvelope className="card-contact-icon" />
                      {client.email}
                    </span>
                    <span className="card-client-phone">
                      📞 {client.phone}
                    </span>
                    <span className="card-client-date">
                      <FaRegCalendarAlt className="card-contact-icon" />
                      {client.date}
                    </span>
                  </div>
                  <div className="card-client-stats">
                    <span className="stat missions">{client.services} Services demandés</span>
                    <span className="stat reports">{client.reports} Signalements</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
      <div className="clients-table-wrapper">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedClients.length === getFilteredAndSortedClients().length && getFilteredAndSortedClients().length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th>ID</th>
              <th>Nom et prénom</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Type</th>
              <th>Date d'inscription</th>
              <th>Services demandés</th>
              <th>Signalements</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clientsLoading ? (
              <tr className="loading-row">
                <td colSpan="11">
                  <div className="loading-spinner"></div>
                  <p>Chargement des clients...</p>
                </td>
              </tr>
            ) : getFilteredAndSortedClients().length === 0 ? (
              <tr className="empty-row">
                <td colSpan="11">Aucun client trouvé</td>
              </tr>
            ) : (
              getFilteredAndSortedClients().map((client) => {
                const avatar = client.avatar && client.avatarColor 
                  ? { avatar: client.avatar, avatarColor: client.avatarColor }
                  : generateAvatar(client.name);
                
                return (
                  <tr key={client.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={() => handleSelectClient(client.id)}
                      />
                    </td>
                    <td>
                      <span className="client-id">{client.displayId}</span>
                    </td>
                    <td>
                      <div className="name-avatar">
                        <div 
                          className="avatar-circle" 
                          style={{ backgroundColor: avatar.avatarColor }}
                        >
                          {avatar.avatar}
                        </div>
                        <span>{client.name}</span>
                      </div>
                    </td>
                    <td>{client.email}</td>
                    <td>{client.phone}</td>
                    <td>{client.type}</td>
                    <td>{client.date}</td>
                    <td>{client.services}</td>
                    <td>{client.reports}</td>
                    <td>
                      <span className={`status-badge ${client.status.toLowerCase() === 'suspendu' ? 'status-inactive' : 'status-active'}`}>
                        {client.status}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <div className="action-icons">
                          <div
                            className="icon view-icon"
                            onClick={() => navigate(`/clients/${client.id}`)}
                            title="Voir les détails du client"
                          >
                            <FaEye />
                          </div>
                          <div
                            className="icon edit-icon"
                            onClick={() => navigate(`/clients/${client.id}`)}
                            title="Éditer le client"
                          >
                            <FaEdit />
                          </div>
                          <div
                            className="icon message-icon"
                            onClick={() => handleSendMessage(client)}
                            title="Envoyer un message"
                          >
                            <FaComment />
                          </div>
                          <div
                            className={`icon ${client.status === 'Suspendu' ? 'reactivate-icon' : 'suspend-icon'}`}
                            onClick={() => handleSuspendClient(client.id, client.name, client.status)}
                            title={client.status === 'Suspendu' ? 'Réactiver le client' : 'Suspendre le client'}
                          >
                            {client.status === 'Suspendu' ? <FaPlay /> : <FaBan />}
                          </div>
                          <div
                            className="icon delete-icon"
                            onClick={() => handleDeleteClient(client.id, client.name)}
                            title="Supprimer le client"
                          >
                            <FaTrashAlt />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <button 
            className="pagination-btn"
            disabled={pagination.page <= 1}
            onClick={() => updateFilters({ page: pagination.page - 1 })}
          >
            <FaChevronLeft size={14} />
            Précédent
          </button>
          
          <span className="pagination-info">
            Page {pagination.page} sur {pagination.totalPages} ({pagination.total} clients)
          </span>
          
          <button 
            className="pagination-btn"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => updateFilters({ page: pagination.page + 1 })}
          >
            Suivant
            <FaChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Modal de confirmation */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmAction}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type === 'delete' ? 'danger' : 'warning'}
        confirmText={
          confirmModal.type === 'delete' ? 'Supprimer' :
          confirmModal.type === 'suspend' ? 'Suspendre' : 'Réactiver'
        }
        cancelText="Annuler"
      />

      {/* Modal Message */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        client={selectedClient}
        type="message"
      />
    </div>
  );
};

export default Clients;